/**
 * HoleScale Role-Based Access Control Configuration
 * 
 * This file is the SINGLE SOURCE OF TRUTH for all role-related constants,
 * permissions, and helper functions. All role strings should be imported
 * from this file, never hardcoded.
 * 
 * @module config/roles
 */

/**
 * User role type - matches the app_role enum in database
 */
export type UserRole = 'buyer' | 'supplier' | 'admin' | 'support';

/**
 * Canonical role constants - use these instead of hardcoded strings
 */
export const USER_ROLES = {
  BUYER: 'buyer',
  SUPPLIER: 'supplier',
  ADMIN: 'admin',
  SUPPORT: 'support',
} as const;

/**
 * Type guard to check if a string is a valid role
 */
export function isValidRole(role: string): role is UserRole {
  return Object.values(USER_ROLES).includes(role as UserRole);
}

/**
 * Role permissions configuration
 * Defines what each role can and cannot do
 */
export const ROLE_PERMISSIONS = {
  [USER_ROLES.BUYER]: {
    // Data Access
    canViewSuppliers: true,
    canViewAllProducts: true,
    canViewSupplierProfiles: true,
    canViewReviews: true,
    
    // RFQ Management
    canCreateRFQs: true,
    canViewOwnRFQs: true,
    canUpdateOwnRFQs: true,
    canDeleteOwnRFQs: true,
    
    // Quote Management
    canViewQuotesOnOwnRFQs: true,
    canCompareQuotes: true,
    
    // Order Management
    canPlaceOrders: true,
    canViewOwnOrders: true,
    canUpdateOwnOrders: true,
    
    // Reviews
    canWriteReviews: true,
    canUpdateOwnReviews: true,
    
    // Messaging
    canMessageSuppliers: true,
    canViewOwnMessages: true,
    
    // Portal Access
    canAccessBuyerDashboard: true,
    canAccessSupplierDashboard: false,
    canAccessAdminPanel: false,
    
    // Default Routes
    defaultLandingPage: '/buyer/dashboard',
    profileRoute: '/buyer/profile',
    settingsRoute: '/buyer/settings',
    ordersRoute: '/buyer/orders',
    messagesRoute: '/buyer/messages',
    rfqsRoute: '/buyer/rfqs',
  },
  
  [USER_ROLES.SUPPLIER]: {
    // Data Access
    canViewSuppliers: false, // Cannot view other suppliers
    canViewAllProducts: false, // Only their own
    canViewOwnProducts: true,
    canViewRFQs: true, // Can view all RFQs to respond
    canViewSupplierProfiles: false, // Cannot view other suppliers
    
    // RFQ Management
    canCreateRFQs: false,
    canViewAllRFQs: true, // To respond with quotes
    canUpdateRFQs: false,
    
    // Quote Management
    canSubmitQuotes: true,
    canViewOwnQuotes: true,
    canUpdateOwnQuotes: true,
    canDeleteOwnQuotes: true,
    
    // Product Management
    canManageProducts: true,
    canCreateProducts: true,
    canUpdateOwnProducts: true,
    canDeleteOwnProducts: true,
    
    // Order Management
    canViewOwnOrders: true, // Orders where they are supplier
    canUpdateOwnOrders: true,
    canFulfillOrders: true,
    
    // Reviews
    canViewReviews: true, // Reviews about them
    canRespondToReviews: true,
    canWriteReviews: false,
    
    // Messaging
    canMessageBuyers: true,
    canViewOwnMessages: true,
    
    // Portal Access
    canAccessBuyerDashboard: false,
    canAccessSupplierDashboard: true,
    canAccessAdminPanel: false,
    
    // Default Routes
    defaultLandingPage: '/supplier/dashboard',
    profileRoute: '/supplier/profile',
    settingsRoute: '/supplier/settings',
    ordersRoute: '/supplier/orders',
    messagesRoute: '/supplier/messages',
    rfqsRoute: '/supplier/rfq-marketplace',
    quotesRoute: '/supplier/quotes',
    productsRoute: '/supplier/products',
  },
  
  [USER_ROLES.ADMIN]: {
    // Data Access - Admins can see everything
    canViewEverything: true,
    canViewSuppliers: true,
    canViewAllProducts: true,
    canViewAllQuotes: true,
    canViewAllUsers: true,
    canViewAllMessages: true,
    
    // User Management
    canManageUsers: true,
    canCreateUsers: true,
    canUpdateUsers: true,
    canDeleteUsers: true,
    canAssignRoles: true,
    canLockAccounts: true,
    
    // Supplier Management
    canManageSuppliers: true,
    canApproveSuppliers: true,
    canRejectSuppliers: true,
    canVerifySuppliers: true,
    
    // Product Management
    canManageAllProducts: true,
    canApproveProducts: true,
    canRejectProducts: true,
    
    // RFQ Management
    canViewAllRFQs: true,
    canManageAllRFQs: true,
    
    // Order Management
    canViewAllOrders: true,
    canManageAllOrders: true,
    
    // Analytics
    canViewAnalytics: true,
    canViewFinancials: true,
    canViewReports: true,
    
    // Portal Access - Admins can access all portals (bypass)
    canAccessBuyerDashboard: true, // View only
    canAccessSupplierDashboard: true, // View only
    canAccessAdminPanel: true,
    
    // Default Routes
    defaultLandingPage: '/admin/dashboard',
    profileRoute: '/admin/profile',
    settingsRoute: '/admin/settings',
    ordersRoute: '/admin/orders',
    messagesRoute: '/admin/messages',
    usersRoute: '/admin/users',
    analyticsRoute: '/admin/analytics',
  },
  
  [USER_ROLES.SUPPORT]: {
    // Data Access - Support can view most things
    canViewEverything: true,
    canViewSuppliers: true,
    canViewAllProducts: true,
    canViewAllRFQs: true,
    canViewAllQuotes: true,
    canViewAllOrders: true,
    canViewAllUsers: true,
    canViewAllMessages: true,
    
    // User Management - Limited
    canManageUsers: false,
    canViewUsers: true,
    canUpdateUsers: false, // Read-only
    canDeleteUsers: false,
    canAssignRoles: false,
    canLockAccounts: false,
    
    // Support Tickets
    canManageSupportTickets: true,
    canViewSupportTickets: true,
    canRespondToTickets: true,
    
    // Portal Access
    canAccessBuyerDashboard: true, // View only
    canAccessSupplierDashboard: true, // View only
    canAccessAdminPanel: true, // Limited access
    
    // Default Routes
    defaultLandingPage: '/admin/dashboard',
    profileRoute: '/admin/profile',
    settingsRoute: '/admin/settings',
  },
} as const;

/**
 * Check if a role has a specific permission
 * 
 * @param role - The user's role
 * @param permission - The permission to check
 * @returns true if the role has the permission
 * 
 * @example
 * hasPermission('buyer', 'canCreateRFQs') // true
 * hasPermission('supplier', 'canCreateRFQs') // false
 */
export function hasPermission(role: UserRole, permission: keyof typeof ROLE_PERMISSIONS[UserRole]): boolean {
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  // Check if permission exists and is true
  if (permission in permissions) {
    return permissions[permission as keyof typeof permissions] === true;
  }
  
  // Check for canViewEverything (admin/support)
  if ('canViewEverything' in permissions && permissions.canViewEverything === true) {
    return true;
  }
  
  return false;
}

/**
 * Get the default landing page for a role
 * 
 * @param role - The user's role
 * @returns The default route for the role
 * 
 * @example
 * getDefaultRoute('buyer') // '/buyer/dashboard'
 * getDefaultRoute('supplier') // '/supplier/dashboard'
 */
export function getDefaultRoute(role: UserRole): string {
  return ROLE_PERMISSIONS[role]?.defaultLandingPage || '/';
}

/**
 * Get the profile route for a role
 * 
 * @param role - The user's role
 * @returns The profile route for the role
 */
export function getProfileRoute(role: UserRole): string {
  return ROLE_PERMISSIONS[role]?.profileRoute || '/profile';
}

/**
 * Get the settings route for a role
 * 
 * @param role - The user's role
 * @returns The settings route for the role
 */
export function getSettingsRoute(role: UserRole): string {
  return ROLE_PERMISSIONS[role]?.settingsRoute || '/settings';
}

/**
 * Get the orders route for a role
 * 
 * @param role - The user's role
 * @returns The orders route for the role
 */
export function getOrdersRoute(role: UserRole): string {
  return ROLE_PERMISSIONS[role]?.ordersRoute || '/orders';
}

/**
 * Get the messages route for a role
 * 
 * @param role - The user's role
 * @returns The messages route for the role
 */
export function getMessagesRoute(role: UserRole): string {
  return ROLE_PERMISSIONS[role]?.messagesRoute || '/messages';
}

/**
 * Get role priority for multi-role users
 * Higher priority roles are used as the "current" role
 * 
 * @param roles - Array of user roles
 * @returns The highest priority role, or null if no roles
 */
export function getPrimaryRole(roles: UserRole[]): UserRole | null {
  if (roles.length === 0) return null;
  
  // Priority order: Admin > Supplier > Buyer > Support
  if (roles.includes(USER_ROLES.ADMIN)) return USER_ROLES.ADMIN;
  if (roles.includes(USER_ROLES.SUPPLIER)) return USER_ROLES.SUPPLIER;
  if (roles.includes(USER_ROLES.BUYER)) return USER_ROLES.BUYER;
  if (roles.includes(USER_ROLES.SUPPORT)) return USER_ROLES.SUPPORT;
  
  return roles[0]; // Fallback to first role
}

/**
 * Get human-readable role label
 * 
 * @param role - The user's role
 * @returns Human-readable label
 */
export function getRoleLabel(role: UserRole): string {
  const labels: Record<UserRole, string> = {
    [USER_ROLES.BUYER]: 'Buyer Account',
    [USER_ROLES.SUPPLIER]: 'Supplier Account',
    [USER_ROLES.ADMIN]: 'Admin Account',
    [USER_ROLES.SUPPORT]: 'Support Account',
  };
  
  return labels[role] || role;
}

/**
 * Get role icon name (for UI components)
 * 
 * @param role - The user's role
 * @returns Icon name
 */
export function getRoleIcon(role: UserRole): string {
  const icons: Record<UserRole, string> = {
    [USER_ROLES.BUYER]: 'ShoppingCart',
    [USER_ROLES.SUPPLIER]: 'Package',
    [USER_ROLES.ADMIN]: 'Shield',
    [USER_ROLES.SUPPORT]: 'Headphones',
  };
  
  return icons[role] || 'User';
}

/**
 * Check if a role can access a specific portal
 * 
 * @param role - The user's role
 * @param portal - The portal to check ('buyer' | 'supplier' | 'admin')
 * @returns true if the role can access the portal
 */
export function canAccessPortal(role: UserRole, portal: 'buyer' | 'supplier' | 'admin'): boolean {
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  switch (portal) {
    case 'buyer':
      return permissions.canAccessBuyerDashboard === true;
    case 'supplier':
      return permissions.canAccessSupplierDashboard === true;
    case 'admin':
      return permissions.canAccessAdminPanel === true;
    default:
      return false;
  }
}

/**
 * Get all roles that can access a specific portal
 * 
 * @param portal - The portal to check
 * @returns Array of roles that can access the portal
 */
export function getRolesForPortal(portal: 'buyer' | 'supplier' | 'admin'): UserRole[] {
  return Object.values(USER_ROLES).filter(role => canAccessPortal(role, portal));
}

